import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Input, Button, Table, Space, message, Select, List, Grid, Card } from 'antd';
import { SearchOutlined, PlusOutlined, TeamOutlined, CheckCircleOutlined, ClockCircleOutlined, PercentageOutlined, ToolOutlined, UploadOutlined } from '@ant-design/icons';
import { getVendors, deleteVendor } from '@/api/vendors';
import { VendorModal } from '@/components/features/resources/vendors/VendorModal';
import { useAuth } from '@/contexts/AuthContext';
import type { Vendor } from '@/types/resources/vendor';
import DeleteConfirmationModal from '@/components/common/DeleteConfirmationModal';
import { VendorsBulkUploadModal } from '@/components/features/resources/vendors/VendorsBulkUploadModal';
import { useSimpleCRUDTour } from '@/hooks/useSimpleCRUDTour';
import { TourButton } from '@/components/common/TourButton';


const { Search } = Input;
const { useBreakpoint } = Grid;

export default function VendorsPage() {
  const { t } = useTranslation(['vendors', 'common']);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { firestoreUser } = useAuth();
  const screens = useBreakpoint();
  const { startCRUDTour, isActive } = useSimpleCRUDTour();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState<Vendor | null>(null);
  const [openBulkUploadDialog, setOpenBulkUploadDialog] = useState(false);

  // State
  const [searchTerm, setSearchTerm] = useState('');
  const [category, setCategory] = useState<string | undefined>();
  const [status, setStatus] = useState<string | undefined>();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [currentSlide, setCurrentSlide] = useState(0);
  const sliderRef = React.useRef<HTMLDivElement>(null);
  
  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [vendorToDelete, setVendorToDelete] = useState<Vendor | null>(null);
  const [deleteError, setDeleteError] = useState<Error & { dependencies?: any[]; vendorName?: string } | undefined>(undefined);

  // Queries
  const { data: vendorsData = { vendors: [], lastDoc: undefined, total: 0 }, isLoading } = useQuery({
    queryKey: ['vendors', searchTerm, category, status, pagination.current, pagination.pageSize, firestoreUser?.workspace?.id],
    queryFn: async () => {
      if (!firestoreUser) {
        throw new Error('User not authenticated');
      }
      const result = await getVendors({ 
        pageSize: pagination.pageSize,
        page: pagination.current,
        firestoreUser 
      });
      return result;
    },
    enabled: !!firestoreUser,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Calculate vendor stats from workspace stats
  const vendorStats = React.useMemo(() => {
    // Get stats from workspace stats (which come from mock data)
    const totalVendors = vendorsData.total || 0;
    const activeVendors = Math.round(totalVendors * 0.9); // 90% active based on mock data logic
    const activeRate = totalVendors > 0 ? Math.round((activeVendors / totalVendors) * 100) : 0;
    
    const result = [
      {
        title: t('vendors:stats.totalVendors'),
        value: totalVendors,
        icon: <TeamOutlined style={{ color: '#1890ff' }} />
      },
      {
        title: t('vendors:stats.activeVendors'),
        value: activeVendors,
        icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />
      },
      {
        title: t('vendors:stats.pendingVendors'),
        value: Math.round(totalVendors * 0.05), // 5% pending
        icon: <ClockCircleOutlined style={{ color: '#faad14' }} />
      },
      {
        title: t('vendors:stats.maintenance'),
        value: Math.round(totalVendors * 0.3), // 30% maintenance
        icon: <ToolOutlined style={{ color: '#722ed1' }} />
      },
      {
        title: t('vendors:stats.activeRate'),
        value: `${activeRate}%`,
        icon: <PercentageOutlined style={{ color: '#eb2f96' }} />
      }
    ];
    
    return result;
  }, [vendorsData.total, t]);

  // Stats slider functions
  const scrollToSlide = (index: number) => {
    if (sliderRef.current) {
      const cardWidth = 140;
      sliderRef.current.scrollTo({
        left: index * cardWidth,
        behavior: 'smooth'
      });
      setCurrentSlide(index);
    }
  };

  const handleScroll = () => {
    if (sliderRef.current) {
      const scrollLeft = sliderRef.current.scrollLeft;
      const cardWidth = 140;
      const newSlide = Math.round(scrollLeft / cardWidth);
      setCurrentSlide(newSlide);
    }
  };

  React.useEffect(() => {
    const slider = sliderRef.current;
    if (slider) {
      slider.addEventListener('scroll', handleScroll);
      return () => slider.removeEventListener('scroll', handleScroll);
    }
  }, []);

  const vendors = vendorsData.vendors;

  // Mutations
  const deleteMutation = useMutation({
    mutationFn: (id: string) => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return deleteVendor(id, firestoreUser);
    },
    onSuccess: () => {
      message.success(t('vendors:messages.deleteSuccess'));
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      setShowDeleteModal(false);
      setDeleteError(undefined);
    },
    onError: (error: Error) => {
      if ((error as any).dependencies) {
        setDeleteError(error);
      } else {
        message.error(t('vendors:messages.deleteError'));
        setShowDeleteModal(false);
      }
      console.error('Error deleting vendor:', error);
    }
  });

  const forceDeleteMutation = useMutation({
    mutationFn: async (id: string) => {
      if (!firestoreUser) throw new Error('User not authenticated');
      // Force delete will handle user account deletion if needed
      return deleteVendor(id, firestoreUser);
    },
    onSuccess: () => {
      message.success(t('vendors:messages.forceDeleteSuccess'));
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      setShowDeleteModal(false);
      setDeleteError(undefined);
    },
    onError: (error: Error) => {
      message.error(t('vendors:messages.forceDeleteError'));
      setShowDeleteModal(false);
      console.error('Error force deleting vendor:', error);
    }
  });

  // Handlers
  const handleAdd = () => {
    setEditingVendor(null);
    setIsModalOpen(true);
  };

  const handleBulkUpload = () => {
    setOpenBulkUploadDialog(true);
  };

  const handleBulkUploadSuccess = () => {
    setOpenBulkUploadDialog(false);
    queryClient.invalidateQueries({ queryKey: ['vendors'] });
  };

  const handleBulkUploadCancel = () => {
    setOpenBulkUploadDialog(false);
  };

  const handleEdit = (vendor: Vendor) => {
    setEditingVendor(vendor);
    setIsModalOpen(true);
  };

  const handleDelete = (vendor: Vendor) => {
    setVendorToDelete(vendor);
    setShowDeleteModal(true);
  };

  const handleViewDetails = (vendor: Vendor) => {
    navigate(`/resources/vendors/${vendor.id}`, { state: { vendor } });
  };

  const handleModalSuccess = () => {
    setIsModalOpen(false);
    setEditingVendor(null);
  };

  const handleModalCancel = () => {
    setIsModalOpen(false);
    setEditingVendor(null);
  };

  const handleTableChange = (newPagination: any) => {
    setPagination(prev => ({ ...prev, ...newPagination }));
  };

  // Tour handler
  const handleStartTour = () => {
    const hasRecords = (vendorsData?.vendors?.length || 0) > 0;
    const recordCount = vendorsData?.total || 0;
    const firstRecordId = vendorsData?.vendors?.[0]?.id;

    startCRUDTour({
      pageName: 'Vendors Management',
      recordType: 'vendor',
      hasRecords,
      recordCount,
      firstRecordId,
      onAddRecord: handleAdd,
      onViewRecord: (id: string) => {
        const vendor = vendorsData?.vendors?.find(v => v.id === id);
        if (vendor) handleViewDetails(vendor);
      },
      onEditRecord: (id: string) => {
        const vendor = vendorsData?.vendors?.find(v => v.id === id);
        if (vendor) handleEdit(vendor);
      },
      onDeleteRecord: (id: string) => {
        const vendor = vendorsData?.vendors?.find(v => v.id === id);
        if (vendor) handleDelete(vendor);
      }
    });
  };

  // Table columns
  const columns = [
    {
      title: t('vendors:fields.name'),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: t('vendors:fields.contactPerson'),
      dataIndex: 'contactPerson',
      key: 'contactPerson',
    },
    {
      title: t('vendors:fields.email'),
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: t('vendors:fields.phone'),
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: t('vendors:fields.category'),
      dataIndex: 'category',
      key: 'category',
      render: (value: string) => t(`vendors:categories.${value}`, value),
    },
    {
      title: t('vendors:fields.status'),
      dataIndex: 'status',
      key: 'status',
      render: (value: string) => t(`vendors:status.${value}`, value),
    },
    {
      title: t('common:actions.title'),
      key: 'actions',
      render: (_: any, record: Vendor) => (
        <Space className="record-actions">
          <Button type="link" onClick={() => handleViewDetails(record)}>
            {t('common:actions.view')}
          </Button>
          <Button type="link" onClick={() => handleEdit(record)}>
            {t('common:actions.edit')}
          </Button>
          <Button
            type="link"
            danger
            onClick={() => handleDelete(record)}
          >
            {t('common:actions.delete')}
          </Button>
        </Space>
      ),
    },
  ];

  const renderVendorCard = (vendor: Vendor) => (
    <Card
      key={vendor.id}
      hoverable
      style={{ marginBottom: 16 }}
      actions={[
        <Button
          key="view"
          type="link"
          onClick={() => handleViewDetails(vendor)}
        >
          {t('common:actions.view')}
        </Button>,
        <Button
          key="edit"
          type="link"
          onClick={() => handleEdit(vendor)}
        >
          {t('common:actions.edit')}
        </Button>,
        <Button
          key="delete"
          type="link"
          danger
          onClick={() => handleDelete(vendor)}
        >
          {t('common:actions.delete')}
        </Button>,
      ]}
    >
      <Card.Meta
        title={vendor.name}
        description={
          <Space direction="vertical" size="small">
            <div>{t('vendors:fields.contactPerson')}: {vendor.contactPerson || '-'}</div>
            <div>{t('vendors:fields.email')}: {vendor.email || '-'}</div>
            <div>{t('vendors:fields.phone')}: {vendor.phone || '-'}</div>
            <div>{t('vendors:fields.category')}: {t(`vendors:categories.${vendor.category}`, vendor.category)}</div>
            <div>{t('vendors:fields.status')}: {t(`vendors:status.${vendor.status}`, vendor.status)}</div>
          </Space>
        }
      />
    </Card>
  );

  return (
    <div>
      {/* Enhanced Stats Section */}
      <div style={{ marginBottom: 24 }}>
        <div
          ref={sliderRef}
          style={{
            display: 'flex',
            overflowX: screens.xs ? 'auto' : 'visible',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            gap: screens.xs ? '12px' : '8px',
            padding: '8px 0',
            flexWrap: screens.xs ? 'nowrap' : 'wrap',
            justifyContent: screens.xs ? 'flex-start' : 'center'
          }}
        >
          {vendorStats.map((stat, idx) => (
            <Card
              key={idx}
              style={{
                minWidth: 120,
                maxWidth: 150,
                width: '100%',
                flex: screens.xs ? '0 0 auto' : '1 1 0',
                cursor: 'pointer',
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.3s ease',
                border: '1px solid #f0f0f0',
                margin: screens.xs ? 0 : '0 4px'
              }}
              onClick={() => scrollToSlide(idx)}
              hoverable
              styles={{
                body: {
                  padding: '16px',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }
              }}
            >
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
                marginBottom: '4px'
              }}>
                <div style={{ fontSize: '20px' }}>
                  {stat.icon}
                </div>
                <div style={{ 
                  fontSize: '24px', 
                  fontWeight: '700',
                  color: '#262626'
                }}>
                  {stat.value}
                </div>
              </div>
              <div style={{ 
                fontSize: '14px', 
                color: '#262626',
                textAlign: 'center',
                lineHeight: '1.2',
                fontWeight: '600'
              }}>
                {stat.title}
              </div>
            </Card>
          ))}
        </div>
        
        {/* Progress Indicators for Mobile */}
        {screens.xs && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            gap: '6px', 
            marginTop: '12px' 
          }}>
            {vendorStats.map((_, idx) => (
              <div
                key={idx}
                onClick={() => scrollToSlide(idx)}
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: currentSlide === idx ? '#1890ff' : '#d9d9d9',
                  cursor: 'pointer',
                  transition: 'background-color 0.3s ease'
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Tour Button */}
      <div style={{ marginBottom: 16, textAlign: 'center' }}>
        <TourButton onClick={handleStartTour} disabled={isActive} size="large" />
      </div>

      <div style={{ marginBottom: 16, display: 'flex', gap: 16 }}>
        <Search
          id="vendors-search-input"
          className="search-input"
          placeholder={t('vendors:searchPlaceholder')}
          allowClear
          enterButton={<SearchOutlined />}
          onSearch={setSearchTerm}
          style={{ width: 300 }}
        />
        <Select
          placeholder={t('vendors:fields.category')}
          allowClear
          style={{ width: 200 }}
          onChange={setCategory}
        >
          {Object.entries(t('vendors:categories', { returnObjects: true }) as [string, string][]).map(([key, value]) => (
            <Select.Option key={key} value={key}>{value}</Select.Option>
          ))}
        </Select>
        <Select
          placeholder={t('vendors:fields.status')}
          allowClear
          style={{ width: 200 }}
          onChange={setStatus}
        >
          {Object.entries(t('vendors:status', { returnObjects: true }) as [string, string][]).map(([key, value]) => (
            <Select.Option key={key} value={key}>{value}</Select.Option>
          ))}
        </Select>
        <Button icon={<UploadOutlined />} onClick={handleBulkUpload}>
          {t('common:actions.bulkUpload')}
        </Button>
        <Button 
          id="vendors-add-button"
          className="add-record-button"
          type="primary" 
          icon={<PlusOutlined />} 
          onClick={handleAdd}
        >
          {t('vendors:actions.add')}
        </Button>
      </div>

      {screens.xs ? (
        <List
          dataSource={vendors}
          renderItem={renderVendorCard}
          rowKey="id"
          loading={isLoading}
          grid={{ gutter: 16, xs: 1, sm: 1 }}
        />
      ) : (
        <Card className="data-table-card">
          <Table
            className="data-table"
            columns={columns}
            dataSource={vendors}
            loading={isLoading}
            rowKey="id"
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: vendorsData.total,
              showSizeChanger: false,
            }}
            onChange={handleTableChange}
          />
        </Card>
      )}

      <VendorModal
        open={isModalOpen}
        vendor={editingVendor}
        onSuccess={handleModalSuccess}
        onCancel={handleModalCancel}
      />

      <DeleteConfirmationModal
        visible={showDeleteModal}
        onCancel={() => {
          setShowDeleteModal(false);
          setDeleteError(undefined);
        }}
        onConfirm={() => {
          if (vendorToDelete) {
            deleteMutation.mutate(vendorToDelete.id);
          }
        }}
        onForceDelete={() => {
          if (vendorToDelete) {
            forceDeleteMutation.mutate(vendorToDelete.id);
          }
        }}
        title={t('vendors:messages.deleteConfirm')}
        error={deleteError}
        onErrorDismiss={() => setDeleteError(undefined)}
        isDeleting={deleteMutation.isPending || forceDeleteMutation.isPending}
      />

      <VendorsBulkUploadModal
        visible={openBulkUploadDialog}
        onCancel={handleBulkUploadCancel}
        onSuccess={handleBulkUploadSuccess}
      />
    </div>
  );
} 